import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStore, websocketUrl } from '@/lib/api';

export interface RealtimeFrame {
  event: string;
  payload: Record<string, unknown>;
}

/**
 * Keeps the React Query cache in sync with server-pushed events and reconnects
 * with exponential backoff when the socket drops (train tunnels, laptop sleep).
 */
export function useRealtime(onEvent?: (frame: RealtimeFrame) => void): void {
  const queryClient = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    let disposed = false;
    let reconnectTimer: number | undefined;
    let pingTimer: number | undefined;

    const connect = () => {
      const token = tokenStore.read()?.access_token;
      if (!token || disposed) return;

      const socket = new WebSocket(websocketUrl(token));
      socketRef.current = socket;

      socket.onopen = () => {
        attemptRef.current = 0;
        pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25_000);
      };

      socket.onmessage = (message) => {
        const frame = JSON.parse(message.data as string) as RealtimeFrame;
        handlerRef.current?.(frame);
        invalidateFor(frame.event);
      };

      socket.onclose = () => {
        window.clearInterval(pingTimer);
        if (disposed) return;
        const delay = Math.min(1000 * 2 ** attemptRef.current, 30_000);
        attemptRef.current += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    const invalidateFor = (event: string) => {
      const map: Record<string, string[][]> = {
        'task.updated': [['tasks'], ['day-plan']],
        'task.created': [['tasks'], ['day-plan']],
        'day_plan.updated': [['day-plan']],
        'suggestions.ready': [['day-plan'], ['suggestions']],
        'client.updated': [['clients']],
        'interaction.created': [['clients'], ['timeline']],
        'call.incoming': [['calls'], ['clients']],
        'conversation.message': [['conversations'], ['conversation'], ['comms-unread']],
        'task.reminder': [['tasks'], ['day-plan'], ['notifications']],
        'ai.suggestion': [['ai-suggestions']],
        'sync.conflict': [['sync-conflicts']],
      };
      for (const key of map[event] ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    };

    connect();
    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      window.clearInterval(pingTimer);
      socketRef.current?.close();
    };
  }, [queryClient]);
}
